import { api } from "@/lib/api";
import { AppointmentType, DayOfWeek, Doctor, Schedule } from "@/types/schedule";
import { useEffect, useState } from "react";
import { toast as sonnerToast } from "sonner";
import { ListaHorariosMedicos } from "./listaHorariosMedicos";

// Função auxiliar para normalizar horário (remove segundos se forem :00)
const normalizarHorario = (horario: string): string => {
  if (!horario) return horario;
  // Se o horário tem formato HH:MM:SS e os segundos são :00, remove os segundos
  if (horario.match(/^\d{2}:\d{2}:00$/)) {
    return horario.substring(0, 5); // Retorna apenas HH:MM
  }
  return horario;
};

export const HorarioMedico = () => {
  // Estados para gerenciamento de horários médicos
  const [doctorsList, setDoctorsList] = useState<Doctor[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [conflictData, setConflictData] = useState<{
    existing: Schedule;
    new: Omit<Schedule, "id">;
  } | null>(null);
  const [pendingSchedule, setPendingSchedule] = useState<Omit<Schedule, "id"> | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleToEdit, setScheduleToEdit] = useState<Schedule | null>(null);

  // Cores padrão para os médicos
  const doctorColors = [
    "#3b82f6", // azul
    "#10b981", // verde
    "#f59e0b", // laranja
    "#ef4444", // vermelho
    "#8b5cf6", // roxo
    "#ec4899", // rosa
    "#06b6d4", // ciano
    "#84cc16", // lima
  ];

  // Função para converter DiaSemana do backend para DayOfWeek do frontend
  const converterDiaSemana = (diaSemana: string): DayOfWeek => {
    const mapeamento: Record<string, DayOfWeek> = {
      'SEGUNDA': 'monday',
      'TERCA': 'tuesday',
      'QUARTA': 'wednesday',
      'QUINTA': 'thursday',
      'SEXTA': 'friday',
      'SABADO': 'saturday',
      'DOMINGO': 'sunday'
    };
    return mapeamento[diaSemana] || 'monday';
  };

  // Função para converter DayOfWeek do frontend para DiaSemana do backend
  const converterDayOfWeekParaDiaSemana = (dayOfWeek: DayOfWeek): string => {
    const mapeamento: Record<DayOfWeek, string> = {
      'monday': 'SEGUNDA',
      'tuesday': 'TERCA',
      'wednesday': 'QUARTA',
      'thursday': 'QUINTA',
      'friday': 'SEXTA',
      'saturday': 'SABADO',
      'sunday': 'DOMINGO'
    };
    return mapeamento[dayOfWeek] || 'SEGUNDA';
  };

  // Função para converter AppointmentType (pode ser ajustada conforme necessário)
  const converterAppointmentType = (): AppointmentType => {
    return 'consultation'; // Valor padrão, pode ser ajustado conforme a necessidade
  };

  // Função para recarregar horários do médico
  const recarregarHorarios = async () => {
    if (!selectedDoctor) return;

    try {
      const idMedico = Number(selectedDoctor.id);
      const response = await api.get(`/api/horarios_medicos?id_medico=${idMedico}`);
      
      if (response?.result) {
        const schedulesConverted: Schedule[] = response.result.map((horario: any) => {
          return {
            id: horario.id.toString(),
            doctorId: horario.id_medico.toString(),
            dayOfWeek: converterDiaSemana(horario.dia_semana),
            startTime: normalizarHorario(horario.hora_inicio),
            endTime: normalizarHorario(horario.hora_fim),
            intervaloMinutos: horario.intervalo_minutos || 30,
            appointmentType: converterAppointmentType(),
            startDate: horario.data_inicio ? new Date(horario.data_inicio) : new Date(),
            endDate: horario.data_fim ? new Date(horario.data_fim) : undefined,
          };
        });
        
        setSchedules(schedulesConverted);
      }
    } catch (error: any) {
      console.error('Erro ao recarregar horários:', error);
    }
  };

  // Buscar médicos da API
  useEffect(() => {
    const carregarMedicos = async () => {
      try {
        const medicosResponse = await api.get('/api/medicos');
        if (medicosResponse?.result) {
          // Converter médicos da API para o formato Doctor
          const doctorsConverted: Doctor[] = medicosResponse.result.map((medico: any, index: number) => {
            const nomeMedico = medico.pessoa?.nome || medico.nome || '';
            const especialidade = medico.especialidade?.descricao || 'Sem especialidade';
            const crm = `${medico.crm || ''}${medico.uf_crm ? `/${medico.uf_crm}` : ''}`;
            
            return {
              id: medico.id.toString(),
              name: nomeMedico,
              specialty: especialidade,
              crm: crm || 'N/A',
              status: medico.ativo ? 'active' : 'inactive',
              color: doctorColors[index % doctorColors.length],
            };
          });
          
          setDoctorsList(doctorsConverted);
        }
      } catch (error: any) {
        sonnerToast.error('Erro ao carregar médicos', {
          description: error.message || 'Não foi possível carregar os médicos'
        });
      }
    };

    carregarMedicos();
  }, []);

  // Buscar horários do médico selecionado
  useEffect(() => {
    const carregarHorarios = async () => {
      if (!selectedDoctor) {
        setSchedules([]);
        return;
      }

      try {
        const idMedico = Number(selectedDoctor.id);
        const response = await api.get(`/api/horarios_medicos?id_medico=${idMedico}`);
        
        if (response?.result) {
          // Converter horários da API para o formato Schedule
          const schedulesConverted: Schedule[] = response.result.map((horario: any) => {
            return {
              id: horario.id.toString(),
              doctorId: horario.id_medico.toString(),
              dayOfWeek: converterDiaSemana(horario.dia_semana),
              startTime: horario.hora_inicio,
              endTime: horario.hora_fim,
              appointmentType: converterAppointmentType(),
              startDate: horario.data_inicio ? new Date(horario.data_inicio) : new Date(),
              endDate: horario.data_fim ? new Date(horario.data_fim) : undefined,
            };
          });
          
          setSchedules(schedulesConverted);
        } else {
          setSchedules([]);
        }
      } catch (error: any) {
        console.error('Erro ao carregar horários:', error);
        sonnerToast.error('Erro ao carregar horários', {
          description: error.message || 'Não foi possível carregar os horários do médico'
        });
        setSchedules([]);
      }
    };

    carregarHorarios();
  }, [selectedDoctor]);

  // Check for time conflicts (exclui o schedule que está sendo editado se fornecido)
  const detectConflict = (newSchedule: Omit<Schedule, "id">, excludeScheduleId?: string): Schedule | null => {
    return schedules.find((existing) => {
      // Ignora o próprio horário se estiver editando
      if (excludeScheduleId && existing.id === excludeScheduleId) return false;
      
      if (existing.doctorId !== newSchedule.doctorId) return false;
      if (existing.dayOfWeek !== newSchedule.dayOfWeek) return false;

      // Check time overlap
      const existingStart = existing.startTime;
      const existingEnd = existing.endTime;
      const newStart = newSchedule.startTime;
      const newEnd = newSchedule.endTime;

      return (
        (newStart >= existingStart && newStart < existingEnd) ||
        (newEnd > existingStart && newEnd <= existingEnd) ||
        (newStart <= existingStart && newEnd >= existingEnd)
      );
    }) || null;
  };

  const handleScheduleAdd = async (newSchedule: Omit<Schedule, "id">) => {
    if (!selectedDoctor) {
      sonnerToast.error('Erro', {
        description: 'Nenhum médico selecionado'
      });
      return;
    }

    // Verifica conflito local antes de salvar (apenas para novos horários)
    if (!scheduleToEdit) {
      const conflict = detectConflict(newSchedule);

      if (conflict) {
        setConflictData({ existing: conflict, new: newSchedule });
        setPendingSchedule(newSchedule);
        return;
      }
    }

    // Salva na API
    try {
      const idMedico = Number(selectedDoctor.id);
      
      // Converte o schedule do frontend para o formato do backend
      const horarioData: any = {
        id_medico: idMedico,
        dia_semana: converterDayOfWeekParaDiaSemana(newSchedule.dayOfWeek),
        hora_inicio: newSchedule.startTime,
        hora_fim: newSchedule.endTime,
        intervalo_minutos: newSchedule.intervaloMinutos || 30,
        data_inicio: newSchedule.startDate ? newSchedule.startDate.toISOString().split('T')[0] : undefined,
        data_fim: newSchedule.endDate ? newSchedule.endDate.toISOString().split('T')[0] : undefined,
      };

      // Adiciona id_tipo_agenda se estiver presente no schedule
      if ((newSchedule as any).id_tipo_agenda) {
        horarioData.id_tipo_agenda = (newSchedule as any).id_tipo_agenda;
      }

      await api.post('/api/horarios_medicos', horarioData);
      
      sonnerToast.success('Horário cadastrado com sucesso!');
      
      // Recarrega os horários do médico
      await recarregarHorarios();
    } catch (error: any) {
      console.error('Erro ao salvar horário:', error);
      sonnerToast.error('Erro ao salvar horário', {
        description: error.message || 'Não foi possível salvar o horário'
      });
    }
  };

  const addSchedule = (schedule: Omit<Schedule, "id">) => {
    const newSchedule: Schedule = {
      ...schedule,
      id: Date.now().toString() + Math.random(),
    };
    setSchedules((prev) => [...prev, newSchedule]);
  };

  const handleConflictResolution = async (action: "replace" | "keep-both" | "cancel") => {
    if (!conflictData || !pendingSchedule || !selectedDoctor) return;

    if (action === "cancel") {
      setConflictData(null);
      setPendingSchedule(null);
      sonnerToast.info("Operação cancelada");
      return;
    }

    try {
      const idMedico = Number(selectedDoctor.id);
      const isEditing = !!scheduleToEdit;

      if (action === "replace") {
        // Remove o horário antigo (não remove se estiver editando o mesmo horário)
        if (!isEditing || conflictData.existing.id !== scheduleToEdit?.id) {
          const idHorarioAntigo = Number(conflictData.existing.id);
          await api.delete(`/api/horarios_medicos/${idHorarioAntigo}`);
        }
      }

      // Salva ou atualiza o horário
      const horarioData = {
        id_medico: idMedico,
        dia_semana: converterDayOfWeekParaDiaSemana(pendingSchedule.dayOfWeek),
        hora_inicio: pendingSchedule.startTime,
        hora_fim: pendingSchedule.endTime,
        data_inicio: pendingSchedule.startDate ? pendingSchedule.startDate.toISOString().split('T')[0] : undefined,
        data_fim: pendingSchedule.endDate ? pendingSchedule.endDate.toISOString().split('T')[0] : undefined,
      };

      if (isEditing && scheduleToEdit) {
        // Atualiza o horário existente
        const idHorario = Number(scheduleToEdit.id);
        await api.put(`/api/horarios_medicos/${idHorario}`, horarioData);
        sonnerToast.success("Horário atualizado com sucesso!");
      } else {
        // Cria novo horário
        await api.post('/api/horarios_medicos', horarioData);
        if (action === "replace") {
          sonnerToast.success("Horário antigo substituído!");
        } else {
          sonnerToast.info("Ambos os horários foram mantidos");
        }
      }

      // Recarrega os horários
      await recarregarHorarios();

      setConflictData(null);
      setPendingSchedule(null);
      setScheduleToEdit(null);
      setIsScheduleModalOpen(false);
    } catch (error: any) {
      console.error('Erro ao resolver conflito:', error);
      sonnerToast.error('Erro ao salvar horário', {
        description: error.message || 'Não foi possível salvar o horário'
      });
    }
  };

  const handleEdit = (schedule: Schedule) => {
    setScheduleToEdit(schedule);
    setIsScheduleModalOpen(true);
  };

  const handleNewSchedule = () => {
    setScheduleToEdit(null);
    setIsScheduleModalOpen(true);
  };

  const handleScheduleUpdate = async (schedule: Schedule) => {
    if (!selectedDoctor) {
      sonnerToast.error('Erro', {
        description: 'Nenhum médico selecionado'
      });
      return;
    }

    // Verifica conflito antes de atualizar (excluindo o próprio horário)
    const { id, ...scheduleWithoutId } = schedule;
    const conflict = detectConflict(scheduleWithoutId, id);

    if (conflict) {
      setConflictData({ existing: conflict, new: scheduleWithoutId });
      setPendingSchedule(scheduleWithoutId);
      return;
    }

    try {
      const idHorario = Number(schedule.id);
      
      // Converte o schedule do frontend para o formato do backend
      const horarioData = {
        id_medico: Number(selectedDoctor.id),
        dia_semana: converterDayOfWeekParaDiaSemana(schedule.dayOfWeek),
        hora_inicio: schedule.startTime,
        hora_fim: schedule.endTime,
        data_inicio: schedule.startDate ? schedule.startDate.toISOString().split('T')[0] : undefined,
        data_fim: schedule.endDate ? schedule.endDate.toISOString().split('T')[0] : undefined,
      };

      await api.put(`/api/horarios_medicos/${idHorario}`, horarioData);
      
      sonnerToast.success('Horário atualizado com sucesso!');
      
      // Recarrega os horários do médico
      await recarregarHorarios();
      
      // Limpa o schedule de edição e fecha o modal
      setScheduleToEdit(null);
      setIsScheduleModalOpen(false);
    } catch (error: any) {
      console.error('Erro ao atualizar horário:', error);
      sonnerToast.error('Erro ao atualizar horário', {
        description: error.message || 'Não foi possível atualizar o horário'
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const idHorario = Number(id);
      await api.delete(`/api/horarios_medicos/${idHorario}`);
      sonnerToast.success("Horário removido com sucesso!");
      
      // Recarrega os horários
      await recarregarHorarios();
    } catch (error: any) {
      console.error('Erro ao remover horário:', error);
      sonnerToast.error('Erro ao remover horário', {
        description: error.message || 'Não foi possível remover o horário'
      });
    }
  };

  const handleScheduleModalOpenChange = (open: boolean) => {
    setIsScheduleModalOpen(open);
    // Limpa o schedule de edição quando o modal é fechado ou quando abre sem scheduleToEdit (novo horário)
    if (!open) {
      setScheduleToEdit(null);
    }
  };

  return (
    <ListaHorariosMedicos
      doctors={doctorsList}
      selectedDoctor={selectedDoctor}
      schedules={schedules}
      conflictData={conflictData}
      isScheduleModalOpen={isScheduleModalOpen}
      scheduleToEdit={scheduleToEdit}
      onDoctorChange={setSelectedDoctor}
      onScheduleAdd={handleScheduleAdd}
      onScheduleUpdate={handleScheduleUpdate}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onConflictResolution={handleConflictResolution}
      onScheduleModalOpenChange={handleScheduleModalOpenChange}
      onNewSchedule={handleNewSchedule}
    />
  );
};
